import React, { lazy, Suspense, useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ComingSoonHero } from '@components/ui/feedback';
import { VerificationsTab } from '@features/eligibility-checks';
import { RevenueAnalyticsPage } from '@features/jotbill-analytics/revenue/RevenueAnalyticsPage';
import { RevenueHomeSection } from '@features/jotbill-analytics/revenue/RevenueHomeSection';
import { useRevenueAccess } from '@features/jotbill-analytics/revenue/useRevenueAccess';
import { useGetEligibilityWorklistQuery } from '@features/patient-coverages';
import { useCan, useSubjectLanguageVariants } from '@features/user';
import { FeatureGates, useFeatureGate } from '@services/statsig';
import { salesBookingUrl } from '@utils/salesBooking';

import { useJotBillAccess } from '../../hooks/useJotBillAccess';

import { ClaimsTab, LegacyClaimsTab } from './claims';
import { UnifiedEncounterWorklistTab } from './encounter-worklist';
import { HomeContainer } from './home';
import { JotBillCenterShell } from './JotBillCenterShell';
import {
  createJotBillProductionTabs,
  isJotBillProductionTabId,
  JOTBILL_DEFAULT_TAB_ID,
  JOTBILL_FALLBACK_TAB_ID,
  JOTBILL_PRODUCTION_TAB_IDS,
  type JotBillProductionTabContent,
  type JotBillProductionTabId,
} from './jotBillCenterTabs';
import { PatientBillingTab } from './patient-billing';

const JOTBILL_LEARN_MORE_URL = salesBookingUrl('jotbill');

const LazyRcmBooth =
  import.meta.env.DEV && import.meta.env.VITE_RCM_SIMULATOR_MODE === 'true'
    ? lazy(() => import('../../rcm-simulator').then(m => ({ default: m.RcmBooth })))
    : null;

export const BillingV2: React.FC = () => {
  const { singularCap, pluralCap } = useSubjectLanguageVariants();
  const { hasAccess: hasJotBillAccess } = useJotBillAccess();
  const hasClaimWorkbench = useFeatureGate(FeatureGates.JOTBILL_CLAIM_WORKBENCH);
  const canManageEligibility = useCan('manage_eligibility');

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const analyticsVisible = useRevenueAccess();
  const analyticsRoute = location.pathname.endsWith('/analytics');
  const requestedTab = analyticsRoute ? 'analytics' : searchParams.get('tab');
  /* Home's promise is "Work this queue", and the queue rail that fulfils it
     lives behind the workbench gate (`EncounterDetailRouteFork` renders the
     legacy encounter page without it). A Home whose button quietly opened one
     claim and stopped would be worse than no Home, so the tab follows the
     same gate, and bare `/jotbill` keeps its pre-Home landing for anyone
     without it. */
  const homeVisible = hasJotBillAccess && hasClaimWorkbench;
  const visibleTabKeys = useMemo(
    () =>
      JOTBILL_PRODUCTION_TAB_IDS.filter(key => {
        if (key === 'analytics') return analyticsVisible;
        if (key === 'verifications') return canManageEligibility;
        if (key === 'home') return homeVisible;
        return true;
      }),
    [canManageEligibility, homeVisible, analyticsVisible],
  );
  const defaultTab = homeVisible ? JOTBILL_DEFAULT_TAB_ID : JOTBILL_FALLBACK_TAB_ID;
  const activeTab =
    isJotBillProductionTabId(requestedTab) && visibleTabKeys.includes(requestedTab)
      ? requestedTab
      : defaultTab;

  useEffect(() => {
    if (requestedTab && !isJotBillProductionTabId(requestedTab)) {
      setSearchParams({}, { replace: true });
    }
  }, [requestedTab, setSearchParams]);

  // Verification queue count for the Eligibility checks tab badge. This tab is
  // available outside the broader JotBill gate, like Patient Billing, but requires
  // the org-resolved manage_eligibility permission and the feature flag.
  const hasVerificationsAccess = canManageEligibility;
  const { data: verificationsData } = useGetEligibilityWorklistQuery(
    { days: 7, perPage: 1 },
    { skip: !hasVerificationsAccess },
  );
  const verificationsNeedsCount = verificationsData?.totals?.awaiting_check ?? 0;

  const handleTabChange = useCallback(
    (tab: JotBillProductionTabId) => {
      if (tab === 'analytics') {
        navigate('/jotbill/analytics');
        return;
      }
      if (analyticsRoute) {
        navigate(tab === defaultTab ? '/jotbill' : `/jotbill?tab=${tab}`);
        return;
      }
      /* Whatever bare `/jotbill` resolves to CLEARS the param rather than
         writing its own name — one address for one page. */
      if (tab === defaultTab) {
        setSearchParams({});
      } else {
        setSearchParams({ tab });
      }
    },
    [defaultTab, setSearchParams, navigate, analyticsRoute],
  );

  const jotBillUpgradeContent = (
    <ComingSoonHero
      title="JotBill"
      description={`Claims, remittances, and work-queue automation for behavioral health practices. Bill ${pluralCap.toLowerCase()} directly today — upgrade to unlock the full billing suite.`}
      primaryAction={{
        label: 'Learn more',
        href: JOTBILL_LEARN_MORE_URL,
        external: true,
        startIcon: null,
      }}
    />
  );

  const content: JotBillProductionTabContent = {
    home: homeVisible ? (
      <>
        <HomeContainer />
        <RevenueHomeSection />
      </>
    ) : (
      jotBillUpgradeContent
    ),
    encounters: hasJotBillAccess ? <UnifiedEncounterWorklistTab /> : jotBillUpgradeContent,
    claims: hasJotBillAccess ? (
      hasClaimWorkbench ? (
        <ClaimsTab />
      ) : (
        <LegacyClaimsTab />
      )
    ) : (
      jotBillUpgradeContent
    ),
    analytics: analyticsVisible ? <RevenueAnalyticsPage embedded /> : null,
    verifications: <VerificationsTab />,
    'patient-billing': <PatientBillingTab />,
  };
  const tabs = createJotBillProductionTabs({
    singularSubjectLabel: singularCap,
    content,
    eligibilityBadgeCount: verificationsNeedsCount,
    eligibilityVisible: canManageEligibility,
    homeVisible,
    analyticsVisible,
  });

  return (
    <>
      <JotBillCenterShell tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
      {LazyRcmBooth && (
        <Suspense fallback={null}>
          <LazyRcmBooth />
        </Suspense>
      )}
    </>
  );
};
